import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { parseCentralDateTime, parsePodSessionNotes } from "../../Components/PodV2/notesUtils";
import {
  getEmailUsername,
  isUnclaimedProctor,
} from "../../Components/PodV2/sessionUtils";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export const CENTRAL_TZ = "America/Chicago";
export const FINISHED_STATUS = "FINISHED";
export const MISSING_VALUE = "—";
export const MAX_INDEX_PAGES_PER_ACTION = 8;
export const DEFAULT_SESSIONS_PER_PAGE = 50;
export const SESSION_PAGE_SIZE_OPTIONS = [25, 50, 100];
export const SORT_NEWEST = "newest";
export const SORT_OLDEST = "oldest";
export const SORT_NAME_ASC = "nameAsc";
export const SORT_NAME_DESC = "nameDesc";
export const SORT_ORG_ASC = "orgAsc";
export const SORT_ORG_DESC = "orgDesc";
export const SORT_COLUMN_DATE = "date";
export const SORT_COLUMN_CANDIDATE = "candidate";
export const SORT_COLUMN_ORGANIZATION = "organization";
export const FILTER_TYPE_PROPERTY = "property";
export const FILTER_TYPE_FLAG = "flag";
export const FILTER_VALUE_ALL = "";

const DATE_DISPLAY_FORMAT = "MM/DD/YYYY";
const TIME_DISPLAY_FORMAT = "h:mm A";
const CENTRAL_DATE_KEY_FORMAT = "YYYY-MM-DD";

export function parseAwsJson(value) {
  let current = value;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (current == null || current === "") {
      return null;
    }

    if (typeof current === "object") {
      return current;
    }

    if (typeof current !== "string") {
      return null;
    }

    const trimmed = current.trim();
    if (!trimmed) {
      return null;
    }

    try {
      current = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  return current;
}

function emptyNotesFields() {
  return {
    examName: "",
    startedAt: "",
    completedAt: "",
    notesText: "",
    flag: MISSING_VALUE,
  };
}

export const FLAG_BACKGROUND_RED = "rgb(193, 24, 24)";
export const FLAG_BACKGROUND_YELLOW = "rgb(243, 195, 0)";
export const FLAG_BACKGROUND_GREEN = "rgb(52, 157, 0)";
export const FLAG_BACKGROUND_ORANGE = "rgb(222, 46, 3)";
export const FLAG_BACKGROUND_GRAY = "rgb(159, 157, 157)";
export const FLAG_BACKGROUND_OTHER = "rgb(145, 38, 134)";
export const FLAG_COLUMN_WIDTH = "100px";
export const FLAG_EXPANDED_ALPHA = 0.15;

const FLAG_RGB_GRAY = [159, 157, 157];
const FLAG_RGB_OTHER = [145, 38, 134];
const FLAG_RGB = {
  red: [193, 24, 24],
  yellow: [243, 195, 0],
  green: [52, 157, 0],
  orange: [222, 46, 3],
  dnc: FLAG_RGB_GRAY,
  mod: FLAG_RGB_GRAY,
  recon: FLAG_RGB_GRAY,
  dns: FLAG_RGB_OTHER,
};

export function capitalizeDisplayName(name) {
  return String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      word
        .split(/([-'])/)
        .map((segment) => {
          if (!segment || segment === "-" || segment === "'") {
            return segment;
          }

          return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
        })
        .join(""),
    )
    .join(" ");
}

export function formatOrganizationDisplay(value) {
  const text = String(value ?? "").trim();
  return text ? text.toUpperCase() : MISSING_VALUE;
}

export function normalizeFlagValue(flag) {
  if (flag == null || flag === "") {
    return MISSING_VALUE;
  }

  if (typeof flag === "boolean") {
    return MISSING_VALUE;
  }

  if (typeof flag !== "string") {
    return MISSING_VALUE;
  }

  const trimmed = flag.trim();
  return trimmed || MISSING_VALUE;
}

function getFlagRgb(flag) {
  const normalized = normalizeFlagValue(flag);
  if (normalized === MISSING_VALUE) {
    return FLAG_RGB_OTHER;
  }

  return FLAG_RGB[normalized.toLowerCase()] || FLAG_RGB_OTHER;
}

export function getFlagBackgroundColor(flag) {
  const [red, green, blue] = getFlagRgb(flag);
  return `rgb(${red}, ${green}, ${blue})`;
}

export function getExpandedCardBackgroundColor(flag) {
  const [red, green, blue] = getFlagRgb(flag);
  return `rgba(${red}, ${green}, ${blue}, ${FLAG_EXPANDED_ALPHA})`;
}

export function extractNotesFields(notes) {
  if (notes == null || notes === "") {
    return emptyNotesFields();
  }

  let notesInput = notes;

  if (typeof notes === "object") {
    try {
      notesInput = JSON.stringify(notes);
    } catch {
      return emptyNotesFields();
    }
  } else if (typeof notes === "string") {
    const trimmed = notes.trim();
    if (!trimmed) {
      return emptyNotesFields();
    }

    try {
      JSON.parse(trimmed);
    } catch {
      return emptyNotesFields();
    }

    notesInput = trimmed;
  } else {
    return emptyNotesFields();
  }

  const parsed = parsePodSessionNotes(notesInput, []);

  return {
    examName: String(parsed.exam ?? "").trim(),
    startedAt: String(parsed.startTime ?? "").trim(),
    completedAt: String(parsed.endTime ?? "").trim(),
    notesText: typeof parsed.notes === "string" ? parsed.notes : "",
    flag: normalizeFlagValue(parsed.flag),
  };
}

export function getValidHttpUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "";
  }

  try {
    const url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return "";
    }
    return url.toString();
  } catch {
    return "";
  }
}

export function getProctorAliases(proctorName, email) {
  return [
    ...new Set(
      [proctorName, getEmailUsername(email)]
        .map((value) => String(value ?? "").trim())
        .filter(Boolean),
    ),
  ];
}

export function normalizePodSession(item) {
  if (!item?.id) {
    return null;
  }

  if (item.status !== FINISHED_STATUS) {
    return null;
  }

  const notesFields = extractNotesFields(item.notes);

  return {
    podSessionId: item.id,
    proctorName: String(item.proctor ?? "").trim(),
    candidateName: String(item.testerName ?? "").trim(),
    organizationId: String(item.organizationId ?? "").trim(),
    examName: notesFields.examName,
    claimedAt: null,
    startedAt: notesFields.startedAt,
    completedAt: notesFields.completedAt,
    notesText: notesFields.notesText,
    flag: notesFields.flag,
    googleMeetLink: getValidHttpUrl(item.googleMeetLink),
    status: FINISHED_STATUS,
    createdAt: item.createdAt || "",
    createdAtCentralDate: item.createdAtCentralDate || "",
  };
}

export function dedupeSessions(sessions) {
  const byId = new Map();

  (sessions ?? []).forEach((session) => {
    if (!session?.podSessionId || byId.has(session.podSessionId)) {
      return;
    }
    byId.set(session.podSessionId, session);
  });

  return [...byId.values()];
}

export function getSessionSortMs(session) {
  const started = parseCentralDateTime(session?.startedAt);
  if (started) {
    return started.valueOf();
  }

  const centralDate = String(session?.createdAtCentralDate ?? "").trim();
  if (centralDate) {
    const parsedDate = dayjs.tz(
      centralDate,
      CENTRAL_DATE_KEY_FORMAT,
      CENTRAL_TZ,
    );
    if (parsedDate.isValid()) {
      return parsedDate.valueOf();
    }
  }

  const createdAtMs = Date.parse(session?.createdAt ?? "");
  if (!Number.isNaN(createdAtMs)) {
    return createdAtMs;
  }

  return 0;
}

export function getCandidateSortName(session) {
  return capitalizeDisplayName(session?.candidateName);
}

export function getOrganizationSortName(session) {
  return String(session?.organizationId ?? "").trim();
}

function compareByNewestThenId(left, right) {
  const dateDiff = getSessionSortMs(right) - getSessionSortMs(left);
  if (dateDiff !== 0) {
    return dateDiff;
  }

  return String(left?.podSessionId ?? "").localeCompare(
    String(right?.podSessionId ?? ""),
  );
}

function compareByTextThenNewest(
  left,
  right,
  getValue,
  descending,
) {
  const textDiff = getValue(left).localeCompare(getValue(right), undefined, {
    sensitivity: "base",
  });

  if (textDiff !== 0) {
    return descending ? -textDiff : textDiff;
  }

  return compareByNewestThenId(left, right);
}

export function getSortColumn(direction = SORT_NEWEST) {
  if (direction === SORT_NAME_ASC || direction === SORT_NAME_DESC) {
    return SORT_COLUMN_CANDIDATE;
  }

  if (direction === SORT_ORG_ASC || direction === SORT_ORG_DESC) {
    return SORT_COLUMN_ORGANIZATION;
  }

  return SORT_COLUMN_DATE;
}

export function isSortAscending(direction = SORT_NEWEST) {
  return (
    direction === SORT_NAME_ASC ||
    direction === SORT_ORG_ASC ||
    direction === SORT_OLDEST
  );
}

export function getNextSortDirection(currentDirection, column) {
  if (column === SORT_COLUMN_CANDIDATE) {
    return currentDirection === SORT_NAME_ASC ? SORT_NAME_DESC : SORT_NAME_ASC;
  }

  if (column === SORT_COLUMN_ORGANIZATION) {
    return currentDirection === SORT_ORG_ASC ? SORT_ORG_DESC : SORT_ORG_ASC;
  }

  return currentDirection === SORT_NEWEST ? SORT_OLDEST : SORT_NEWEST;
}

export function getSortHeaderA11yTitle(column, currentDirection) {
  const nextDirection = getNextSortDirection(currentDirection, column);

  if (column === SORT_COLUMN_CANDIDATE) {
    return nextDirection === SORT_NAME_ASC
      ? "Sort candidate name ascending"
      : "Sort candidate name descending";
  }

  if (column === SORT_COLUMN_ORGANIZATION) {
    return nextDirection === SORT_ORG_ASC
      ? "Sort organization ascending"
      : "Sort organization descending";
  }

  return nextDirection === SORT_NEWEST
    ? "Sort date newest first"
    : "Sort date oldest first";
}

export function sortSessions(sessions, direction = SORT_NEWEST) {
  return [...(sessions ?? [])].sort((left, right) => {
    if (direction === SORT_NAME_ASC || direction === SORT_NAME_DESC) {
      return compareByTextThenNewest(
        left,
        right,
        getCandidateSortName,
        direction === SORT_NAME_DESC,
      );
    }

    if (direction === SORT_ORG_ASC || direction === SORT_ORG_DESC) {
      return compareByTextThenNewest(
        left,
        right,
        getOrganizationSortName,
        direction === SORT_ORG_DESC,
      );
    }

    const sign = direction === SORT_OLDEST ? 1 : -1;
    const diff = getSessionSortMs(left) - getSessionSortMs(right);
    if (diff !== 0) {
      return diff * sign;
    }

    return String(left?.podSessionId ?? "").localeCompare(
      String(right?.podSessionId ?? ""),
    );
  });
}

function collectUniqueSortedValues(values) {
  const seen = new Map();

  values.forEach((value) => {
    const original = String(value ?? "").trim();
    if (!original) {
      return;
    }

    const key = original.toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, original);
    }
  });

  return [...seen.values()].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" }),
  );
}

export function uniqueOrganizationIds(sessions) {
  return collectUniqueSortedValues(
    (sessions ?? []).map((session) => session?.organizationId),
  );
}

export function uniqueFlagValues(sessions) {
  return collectUniqueSortedValues(
    (sessions ?? []).map((session) => {
      const flag = normalizeFlagValue(session?.flag);
      return flag === MISSING_VALUE ? "" : flag;
    }),
  );
}

export function getPropertyFilterOptions(sessions) {
  return [
    { label: "All Properties", value: FILTER_VALUE_ALL },
    ...uniqueOrganizationIds(sessions).map((organizationId) => ({
      label: formatOrganizationDisplay(organizationId),
      value: organizationId,
    })),
  ];
}

export function getFlagFilterOptions(sessions) {
  return [
    { label: "All Flags", value: FILTER_VALUE_ALL },
    ...uniqueFlagValues(sessions).map((flag) => ({
      label: flag,
      value: flag,
    })),
  ];
}

export function nextFilterTypeState(nextType) {
  return {
    filterType:
      nextType === FILTER_TYPE_FLAG ? FILTER_TYPE_FLAG : FILTER_TYPE_PROPERTY,
    filterValue: FILTER_VALUE_ALL,
  };
}

export function getClearedDynamicFilterState() {
  return {
    filterType: FILTER_TYPE_PROPERTY,
    filterValue: FILTER_VALUE_ALL,
  };
}

export function isDefaultDynamicFilter(filterType, filterValue) {
  return (
    filterType === FILTER_TYPE_PROPERTY &&
    String(filterValue ?? "").trim() === FILTER_VALUE_ALL
  );
}

export function isAllFilterValue(filterValue) {
  const value = String(filterValue ?? "").trim().toLowerCase();
  return (
    !value ||
    value === "all" ||
    value === "all properties" ||
    value === "all flags" ||
    value === "property" ||
    value === "flag"
  );
}

export function formatStatisticsFlagLabel(flag) {
  const normalized = String(flag ?? "").trim();
  if (!normalized) {
    return "";
  }

  const key = normalized.toLowerCase();
  if (key === "dns" || key === "dnc" || key === "mod" || key === "recon") {
    return key.toUpperCase();
  }

  if (
    key === "green" ||
    key === "yellow" ||
    key === "orange" ||
    key === "red"
  ) {
    return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
  }

  return capitalizeDisplayName(normalized);
}

export function getStatisticsTitlePrefix(filterType, filterValue) {
  if (isAllFilterValue(filterValue)) {
    return "All";
  }

  if (filterType === FILTER_TYPE_FLAG) {
    return formatStatisticsFlagLabel(filterValue);
  }

  return formatOrganizationDisplay(filterValue);
}

export function getStatisticsTitleText(filterType, filterValue) {
  return `${getStatisticsTitlePrefix(filterType, filterValue)} Session Statistics`;
}

function valuesMatchCaseInsensitive(left, right) {
  return (
    String(left ?? "").trim().toLowerCase() ===
    String(right ?? "").trim().toLowerCase()
  );
}

export function filterSessionsByProperty(sessions, propertyId) {
  if (
    !propertyId ||
    propertyId === FILTER_VALUE_ALL ||
    String(propertyId).trim().toLowerCase() === "all"
  ) {
    return sessions ?? [];
  }

  return (sessions ?? []).filter((session) =>
    valuesMatchCaseInsensitive(session?.organizationId, propertyId),
  );
}

export function filterSessionsByFlag(sessions, flagValue) {
  if (
    !flagValue ||
    flagValue === FILTER_VALUE_ALL ||
    String(flagValue).trim().toLowerCase() === "all"
  ) {
    return sessions ?? [];
  }

  return (sessions ?? []).filter((session) => {
    const flag = normalizeFlagValue(session?.flag);
    if (flag === MISSING_VALUE) {
      return false;
    }

    return valuesMatchCaseInsensitive(flag, flagValue);
  });
}

export function getSessionCalendarDate(session) {
  const started = parseCentralDateTime(session?.startedAt);
  if (started) {
    return started.format(CENTRAL_DATE_KEY_FORMAT);
  }

  const centralDate = String(session?.createdAtCentralDate ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(centralDate)) {
    return centralDate;
  }

  const createdAt = String(session?.createdAt ?? "").trim();
  if (createdAt) {
    const parsedCreatedAt = dayjs(createdAt);
    if (parsedCreatedAt.isValid()) {
      return parsedCreatedAt.tz(CENTRAL_TZ).format(CENTRAL_DATE_KEY_FORMAT);
    }
  }

  return "";
}

export function filterSessionsByDateRange(sessions, startDate, endDate) {
  const start = String(startDate ?? "").trim();
  const end = String(endDate ?? "").trim();

  if (!start && !end) {
    return sessions ?? [];
  }

  return (sessions ?? []).filter((session) => {
    const date = getSessionCalendarDate(session);
    if (!date) {
      return false;
    }
    if (start && date < start) {
      return false;
    }
    if (end && date > end) {
      return false;
    }
    return true;
  });
}

export function getFilteredSessions(
  sessions,
  {
    dateStart = "",
    dateEnd = "",
    filterType = FILTER_TYPE_PROPERTY,
    filterValue = FILTER_VALUE_ALL,
  } = {},
) {
  const datedSessions = filterSessionsByDateRange(
    dedupeSessions(sessions),
    dateStart,
    dateEnd,
  );

  if (filterType === FILTER_TYPE_FLAG) {
    return filterSessionsByFlag(datedSessions, filterValue);
  }

  return filterSessionsByProperty(datedSessions, filterValue);
}

export function getFilteredSortedSessions(
  sessions,
  {
    dateStart = "",
    dateEnd = "",
    filterType = FILTER_TYPE_PROPERTY,
    filterValue = FILTER_VALUE_ALL,
    sortDirection = SORT_NEWEST,
  } = {},
) {
  return sortSessions(
    getFilteredSessions(sessions, {
      dateStart,
      dateEnd,
      filterType,
      filterValue,
    }),
    sortDirection,
  );
}

export const FLAG_CATEGORY_GREEN = "green";
export const FLAG_CATEGORY_YELLOW = "yellow";
export const FLAG_CATEGORY_RED = "red";
export const FLAG_CATEGORY_ORANGE = "orange";
export const FLAG_CATEGORY_DNS = "dns";
export const FLAG_CATEGORY_DNC_GROUP = "dncGroup";
export const FLAG_CATEGORY_OTHER = "other";
export const CLAIMED_AT_FETCH_CONCURRENCY = 4;

export function classifyFlagCategory(flag) {
  const normalized = normalizeFlagValue(flag);
  if (normalized === MISSING_VALUE) {
    return FLAG_CATEGORY_OTHER;
  }

  const key = normalized.toLowerCase();
  if (key === "green") {
    return FLAG_CATEGORY_GREEN;
  }
  if (key === "yellow") {
    return FLAG_CATEGORY_YELLOW;
  }
  if (key === "red") {
    return FLAG_CATEGORY_RED;
  }
  if (key === "orange") {
    return FLAG_CATEGORY_ORANGE;
  }
  if (key === "dns") {
    return FLAG_CATEGORY_DNS;
  }
  if (key === "dnc" || key === "mod" || key === "recon") {
    return FLAG_CATEGORY_DNC_GROUP;
  }

  return FLAG_CATEGORY_OTHER;
}

export function formatCountWithPercent(count, total) {
  const safeCount = Number(count) || 0;
  const safeTotal = Number(total) || 0;
  const percent = safeTotal === 0 ? 0 : (safeCount / safeTotal) * 100;
  return `${safeCount} (${percent.toFixed(1)}%)`;
}

export function calculateSessionStatistics(sessions) {
  const rows = sessions ?? [];
  const counts = {
    green: 0,
    yellow: 0,
    red: 0,
    orange: 0,
    dns: 0,
    dncGroup: 0,
    other: 0,
  };

  rows.forEach((session) => {
    counts[classifyFlagCategory(session?.flag)] += 1;
  });

  const total = rows.length;
  const categoryTotal =
    counts.green +
    counts.yellow +
    counts.red +
    counts.orange +
    counts.dns +
    counts.dncGroup +
    counts.other;

  return {
    total,
    ...counts,
    categoryTotal,
    greenDisplay: formatCountWithPercent(counts.green, total),
    yellowDisplay: formatCountWithPercent(counts.yellow, total),
    redDisplay: formatCountWithPercent(counts.red, total),
    orangeDisplay: formatCountWithPercent(counts.orange, total),
    otherDisplay: formatCountWithPercent(counts.other, total),
  };
}

export function parseClaimedAtMs(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }

  if (/^\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AaPp][Mm])?$/.test(raw)) {
    return null;
  }

  const parsed = dayjs(raw);
  if (!parsed.isValid()) {
    return null;
  }

  return parsed.valueOf();
}

export function getSetupDurationMs(session, claimedAt) {
  const started = parseCentralDateTime(session?.startedAt);
  if (!started) {
    return null;
  }

  const claimedMs = parseClaimedAtMs(claimedAt);
  if (claimedMs == null) {
    return null;
  }

  const duration = started.valueOf() - claimedMs;
  if (duration < 0) {
    return null;
  }

  return duration;
}

export function formatSetupDuration(milliseconds) {
  if (milliseconds == null || !Number.isFinite(milliseconds) || milliseconds < 0) {
    return MISSING_VALUE;
  }

  const totalSeconds = Math.round(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export function formatSetupCoverage(validCount, eligibleCount) {
  if (!eligibleCount) {
    return "No eligible sessions";
  }

  return `Based on ${Number(validCount) || 0} of ${Number(eligibleCount) || 0} eligible sessions`;
}

export const AVERAGE_SETUP_TIME_TOOLTIP =
  "This is the average time between when you claimed a session and when you got the candidate started.";

export function isSetupTimeEligible(session) {
  return classifyFlagCategory(session?.flag) !== FLAG_CATEGORY_DNC_GROUP;
}

export function calculateAverageSetupTime(sessions, claimedAtBySessionId = {}) {
  const eligible = (sessions ?? []).filter(isSetupTimeEligible);
  const cache = claimedAtBySessionId ?? {};
  const durations = [];

  eligible.forEach((session) => {
    const id = session?.podSessionId;
    if (!id || !Object.prototype.hasOwnProperty.call(cache, id)) {
      return;
    }

    const duration = getSetupDurationMs(session, cache[id]);
    if (duration == null) {
      return;
    }

    durations.push(duration);
  });

  const validCount = durations.length;
  const eligibleCount = eligible.length;

  if (!validCount) {
    return {
      averageMs: null,
      formatted: MISSING_VALUE,
      validCount: 0,
      eligibleCount,
      totalCount: eligibleCount,
      coverageText: formatSetupCoverage(0, eligibleCount),
    };
  }

  const averageMs =
    Math.round(
      durations.reduce((sum, duration) => sum + duration, 0) /
        validCount /
        1000,
    ) * 1000;

  return {
    averageMs,
    formatted: formatSetupDuration(averageMs),
    validCount,
    eligibleCount,
    totalCount: eligibleCount,
    coverageText: formatSetupCoverage(validCount, eligibleCount),
  };
}

export function getSessionsMissingClaimedAt(sessions, claimedAtBySessionId = {}) {
  const cache = claimedAtBySessionId ?? {};
  return (sessions ?? []).filter((session) => {
    const id = session?.podSessionId;
    return (
      Boolean(id) &&
      isSetupTimeEligible(session) &&
      !Object.prototype.hasOwnProperty.call(cache, id)
    );
  });
}

export function shouldApplyClaimedAtResult(activeGeneration, resultGeneration) {
  return activeGeneration === resultGeneration;
}

export async function runWithConcurrency(
  items,
  limit,
  worker,
  shouldContinue = () => true,
) {
  const queue = [...(items ?? [])];
  const concurrency = Math.max(1, Number(limit) || 1);
  const workerCount = Math.min(concurrency, queue.length);

  if (!workerCount) {
    return;
  }

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (queue.length && shouldContinue()) {
        const item = queue.shift();
        await worker(item);
      }
    }),
  );
}

export function getVisiblePageCount(filteredCount, sessionsPerPage) {
  const size = Number(sessionsPerPage) || DEFAULT_SESSIONS_PER_PAGE;
  if (!filteredCount || filteredCount <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(filteredCount / size));
}

export function clampVisiblePage(page, filteredCount, sessionsPerPage) {
  const pageCount = getVisiblePageCount(filteredCount, sessionsPerPage);
  const requested = Number(page) || 1;
  if (requested < 1) {
    return 1;
  }
  if (requested > pageCount) {
    return pageCount;
  }
  return requested;
}

export function paginateSessions(filteredAndSorted, page, sessionsPerPage) {
  const size = Number(sessionsPerPage) || DEFAULT_SESSIONS_PER_PAGE;
  const sessions = filteredAndSorted ?? [];
  const safePage = clampVisiblePage(page, sessions.length, size);
  const startIndex = (safePage - 1) * size;

  return {
    page: safePage,
    pageCount: getVisiblePageCount(sessions.length, size),
    startIndex,
    firstVisible: sessions.length === 0 ? 0 : startIndex + 1,
    lastVisible: Math.min(startIndex + size, sessions.length),
    filteredCount: sessions.length,
    visibleSessions: sessions.slice(startIndex, startIndex + size),
  };
}

export function formatShowingCounter({
  page,
  sessionsPerPage,
  filteredCount,
  hasMore,
}) {
  if (!filteredCount) {
    return "";
  }

  const size = Number(sessionsPerPage) || DEFAULT_SESSIONS_PER_PAGE;
  const safePage = clampVisiblePage(page, filteredCount, size);
  const startIndex = (safePage - 1) * size;
  const firstVisible = startIndex + 1;
  const lastVisible = Math.min(startIndex + size, filteredCount);
  const noun = hasMore ? "loaded sessions" : "sessions";

  return `Showing ${firstVisible}–${lastVisible} of ${filteredCount} ${noun}`;
}

export function resolveExpandedSessionId(expandedId, visibleSessions) {
  if (!expandedId) {
    return null;
  }

  const isVisible = (visibleSessions ?? []).some(
    (session) => session.podSessionId === expandedId,
  );

  return isVisible ? expandedId : null;
}

export function formatSessionDate(session) {
  const started = parseCentralDateTime(session?.startedAt);
  if (started) {
    return started.format(DATE_DISPLAY_FORMAT);
  }

  const centralDate = String(session?.createdAtCentralDate ?? "").trim();
  if (centralDate) {
    const parsedDate = dayjs(centralDate, CENTRAL_DATE_KEY_FORMAT, true);
    if (parsedDate.isValid()) {
      return parsedDate.format(DATE_DISPLAY_FORMAT);
    }
  }

  const createdAt = String(session?.createdAt ?? "").trim();
  if (createdAt) {
    const parsedCreatedAt = dayjs(createdAt);
    if (parsedCreatedAt.isValid()) {
      return parsedCreatedAt.tz(CENTRAL_TZ).format(DATE_DISPLAY_FORMAT);
    }
  }

  return MISSING_VALUE;
}

export function formatSessionTime(value) {
  const parsed = parseCentralDateTime(value);
  if (!parsed) {
    return MISSING_VALUE;
  }

  return parsed.format(TIME_DISPLAY_FORMAT);
}

export function formatClaimedAt(value) {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return MISSING_VALUE;
  }

  const parsed = dayjs(raw);
  if (!parsed.isValid()) {
    return MISSING_VALUE;
  }

  return parsed.tz(CENTRAL_TZ).format(TIME_DISPLAY_FORMAT);
}

export function displayOrMissing(value) {
  const text = String(value ?? "").trim();
  return text || MISSING_VALUE;
}

export function getHistoryErrorKind(error) {
  const graphqlType = String(
    error?.errors?.[0]?.errorType ??
      error?.errors?.[0]?.message ??
      error?.message ??
      "unknown",
  );

  if (/unauthor/i.test(graphqlType)) {
    return "authorization";
  }

  if (/network|failed to fetch|timeout/i.test(graphqlType)) {
    return "network";
  }

  return "graphql";
}

export function getHistoryViewState({
  loading,
  searching,
  error,
  sessions,
  hasMore,
}) {
  if (error) {
    return "error";
  }

  if (loading && !searching) {
    return "loading";
  }

  const sessionCount = sessions?.length ?? 0;

  if (searching && sessionCount === 0) {
    return "searching";
  }

  if (sessionCount === 0 && hasMore) {
    return "recent-empty";
  }

  if (sessionCount === 0) {
    return "exhausted";
  }

  return "list";
}

export function appendFinishedSessions(existing, incoming) {
  return dedupeSessions([...(existing ?? []), ...(incoming ?? [])]);
}

export async function searchFinishedSessionsForAlias({
  fetchPage,
  startToken = null,
  maxPages = MAX_INDEX_PAGES_PER_ACTION,
  onSkippedEmptyPage,
} = {}) {
  if (typeof fetchPage !== "function") {
    throw new Error("fetchPage is required");
  }

  let cursor = startToken || null;
  let pagesEvaluated = 0;

  while (pagesEvaluated < maxPages) {
    const page = await fetchPage(cursor);
    pagesEvaluated += 1;

    const items = Array.isArray(page?.items) ? page.items : [];
    const pageToken = page?.nextToken ?? null;
    const sessions = items.map(normalizePodSession).filter(Boolean);

    if (sessions.length > 0) {
      return {
        sessions,
        nextToken: pageToken,
        pagesEvaluated,
        exhausted: !pageToken,
        hitPageLimit: false,
      };
    }

    if (!pageToken) {
      return {
        sessions: [],
        nextToken: null,
        pagesEvaluated,
        exhausted: true,
        hitPageLimit: false,
      };
    }

    cursor = pageToken;
    onSkippedEmptyPage?.({
      pagesEvaluated,
      hasMore: true,
    });
  }

  return {
    sessions: [],
    nextToken: cursor,
    pagesEvaluated,
    exhausted: false,
    hitPageLimit: true,
  };
}

export function extractClaimedAtFromEvents(events, finalProctor) {
  if (isUnclaimedProctor(finalProctor)) {
    return null;
  }

  const expected = String(finalProctor ?? "").trim();
  if (!expected) {
    return null;
  }

  const sorted = [...(events ?? [])].sort((left, right) => {
    const leftMs = Date.parse(left?.timestamp ?? "") || 0;
    const rightMs = Date.parse(right?.timestamp ?? "") || 0;
    return rightMs - leftMs;
  });

  for (const event of sorted) {
    const input = parseAwsJson(event?.input);
    if (!input || typeof input !== "object" || Array.isArray(input)) {
      continue;
    }

    if (!Object.prototype.hasOwnProperty.call(input, "proctor")) {
      continue;
    }

    if (isUnclaimedProctor(input.proctor)) {
      continue;
    }

    if (String(input.proctor).trim() === expected) {
      return event.timestamp || null;
    }
  }

  return null;
}
